/**
 * User Video Service
 *
 * Allows users to add YouTube video URLs to exercises that don't have them.
 * User-contributed videos are stored per-user in Firestore and merged with
 * the static exercise library at runtime.
 */

import { doc, setDoc, deleteDoc, getDoc, collection, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Add or update a user-contributed video for an exercise
 */
export async function addUserVideo(userId, exerciseId, youtubeUrl) {
  if (!userId || !exerciseId || !youtubeUrl) {
    throw new Error('userId, exerciseId, and youtubeUrl are required');
  }

  // Basic YouTube URL validation
  const isValidYouTubeUrl = /^https:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[\w-]+/.test(youtubeUrl);
  if (!isValidYouTubeUrl) {
    throw new Error('Invalid YouTube URL. Must be a youtube.com or youtu.be link.');
  }

  const videoRef = doc(db, 'users', userId, 'exerciseVideos', exerciseId);

  await setDoc(videoRef, {
    youtubeUrl,
    addedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete a user-contributed video
 */
export async function deleteUserVideo(userId, exerciseId) {
  if (!userId || !exerciseId) {
    throw new Error('userId and exerciseId are required');
  }

  const videoRef = doc(db, 'users', userId, 'exerciseVideos', exerciseId);
  await deleteDoc(videoRef);
}

/**
 * Get a user's video for a specific exercise
 */
export async function getUserVideoForExercise(userId, exerciseId) {
  if (!userId || !exerciseId) return null;

  const videoRef = doc(db, 'users', userId, 'exerciseVideos', exerciseId);
  const videoDoc = await getDoc(videoRef);

  if (videoDoc.exists()) {
    return videoDoc.data();
  }
  return null;
}

/**
 * Get all user-contributed videos
 * Returns a Map of exerciseId -> video data
 */
export async function getUserVideos(userId) {
  if (!userId) return new Map();

  const videosRef = collection(db, 'users', userId, 'exerciseVideos');
  const snapshot = await getDocs(videosRef);

  const videos = new Map();
  snapshot.forEach(doc => {
    videos.set(doc.id, doc.data());
  });

  return videos;
}

/**
 * Merge user videos with exercise data
 * User videos take precedence over static videos
 */
export function mergeUserVideos(exercises, userVideos) {
  if (!userVideos || userVideos.size === 0) {
    return exercises;
  }

  return exercises.map(exercise => {
    const userVideo = userVideos.get(exercise.id);
    if (userVideo) {
      return {
        ...exercise,
        media: {
          ...(exercise.media || {}),
          youtubeUrl: userVideo.youtubeUrl,
          userContributed: true, // Flag to show this is user-added
        }
      };
    }
    return exercise;
  });
}
